/**
 * useMessaging — message-library data hook (Greg's lifecycle shape, 2026-07-07)
 * re-grafted onto the live CRUD wiring (#728, 2026-07-07 handback).
 *
 * Reads through the typed client (`listBuilderMessages`); server rows are
 * overlaid onto any locally-known content fields. The demo seed is EMPTY per
 * the seed-guard — rich content fields (subject/perChannel/audience/whatsapp)
 * must come from the mock layer (REQ-017).
 *
 * Mutations call the live createMessage/updateMessage/deleteMessage/
 * duplicateMessage ops, AWAITED, before the cache updates (no-fake-
 * interactivity mandate). Callers surface failures as real error states.
 */
import { t as i18nT } from "../../lib/i18n";
import { useEffect, useState } from "react";
import { listBuilderMessages, apiRaw, createMessage, updateMessage, deleteMessage, duplicateMessage } from "@v0y4ge/api-client";
import type { StudioMessageCreateInput, StudioMessageUpdateInput } from "@v0y4ge/api-contracts";
import type { MessageItem } from "./messaging-seed";
import { logUpsert, logDelete } from "../../lib/audit";
import { warningsOf, type MsgWarning } from "./message-render-api";
import { onTenantChange, onTenantSwitching } from "../../shell/access";

let _cache: MessageItem[] | null = null;
/** Non-null when the last read FAILED (as opposed to returning nothing). The
 *  distinction is the whole point: an empty library and a broken read looked
 *  identical twice in one day, and the second time it read as data loss. */
let _loadError: string | null = null;
/** Warnings named by the LAST write (`warnings[]`, always present now — CC
 *  2026-09-03 §3). Empty when clean. Surfaced by the composer: a field the
 *  platform dropped has to be visible where it was typed, not in a log. */
let _lastWarnings: MsgWarning[] = [];
const _listeners = new Set<() => void>();
const notify = () => _listeners.forEach((fn) => fn());
onTenantSwitching(() => { _cache = null; notify(); });
onTenantChange(() => { void loadAll(); });

let _seq = 0;
const tmpId = () => `m_new_${Date.now().toString(36)}${(_seq++).toString(36)}`;

/** The real message-write contract's `channels` enum only knows
 *  email|sms|whatsapp|push (confirmed live, 2026-07-31 — a write carrying our
 *  UI-only "calendar"/"wallet" pseudo-channels came back as a Zod
 *  invalid_enum_value rejection AFTER a successful 200, which read as "can't
 *  unlock the message"). Calendar/wallet are real in the UI (their own tab,
 *  their own content) but are never valid members of the wire `channels`
 *  array — their presence is carried by `ics`/`wallet` fields instead. Strip
 *  them from the OUTGOING payload only; the local cache keeps the full set so
 *  the channel picker doesn't lose the selection. */
const WIRE_CHANNELS = new Set(["email", "sms", "whatsapp", "push"]);

/* Two-status lifecycle (operator, 2026-08-04): a message is DRAFT or LIVE, nothing else.
   Legacy stored values ("approved", "scheduled", "sent", "awaiting_approval") all read as
   live — normalizing here, at the one read seam, retires them from every screen at once. */
const normStatus = (s: unknown): "draft" | "live" => (s === "draft" ? "draft" : "live");

/* The wire carries only real delivery channels (contract conformance, 2026-08-04); the UI's
   wallet/calendar pseudo-channels are re-derived here from the fields that actually hold their
   content, so chips and composer tabs light up without dirtying the wire enum. */
/* Engine writes stamp ISO timestamps into updatedAt; the column is display text, so ISO strings
   are humanized here at the read seam. Non-ISO values ("Today", "2d ago") pass through. */
const humanWhen = (v: unknown): string => {
  const s = String(v ?? "—");
  if (!/^\d{4}-\d{2}-\d{2}T/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "Just now";
  if (mins < 60) return mins + "m ago";
  if (mins < 60 * 24) return Math.round(mins / 60) + "h ago";
  if (mins < 60 * 24 * 7) return Math.round(mins / (60 * 24)) + "d ago";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const withPseudo = (ch: string[], s: Record<string, any>): string[] => {
  const out = (ch || []).slice();
  if (s.wallet && out.indexOf("wallet") < 0) out.push("wallet");
  if (s.ics && out.indexOf("calendar") < 0) out.push("calendar");
  return out;
};
/* (`wireSafe` retired 2026-09-02 — `wireContent` below does the channel filter
   as part of building the typed payload, so there is one place that decides what
   goes on the wire.) */

/* ── The four writes go through the TYPED ops (2026-09-02, CC build report §2).

   The four hand-rolled `apiRaw` helpers that used to live here are DELETED. The
   comment that justified them — "the typed names are not exported by the real
   client" — was stale: `createMessage` / `updateMessage` / `deleteMessage` /
   `duplicateMessage` are exported from `studio-messages.ts` and barrelled at
   `api-client/src/index.ts:961-967`. Worse, `_restUpdate` PUT the row while the
   contract, the route and the mock all declare **PATCH** (`updateStudioMessage`),
   so every edit 404'd against the real backend and only passed here because the
   serving engine answers PUT and PATCH alike (`serving-engine/browser.js:3334`).

   Two shape rules the typed ops enforce that the raw calls skipped:

   - **Server-owned fields are never sent on update.** `id`, `status`,
     `updatedAt` (and the display-only `updated`) are refused BY NAME in
     `StudioMessageUpdateInput` — the whole point being that assigning
     `status: "live"` would report 200 for a lifecycle change that never
     happened. Lifecycle moves through `command` instead.
   - **Locale content is named.** The composer's `translations` map is mirrored
     into the contract's `localeVariants` / `defaultLocale`.

   Everything else the composer owns (`emailDesign`, `emailHtml`) rides the
   schemas' `.passthrough()` until it is modelled by name. The seven fields the
   backend does not model at all are no longer sent — see `UNMODELLED_FIELDS`. ── */

/** The BCP-47 shape `StudioMessageLocale` accepts. A tag that fails it is left
 *  out of `localeVariants` rather than failing the whole write. */
const localeOk = (tag: string): boolean => /^[a-z]{2,8}(-[A-Za-z0-9]{2,8})*$/.test(tag);

/** The contract models five NAMED channel bodies; our `perChannel` is a loose
 *  Record that also carries the UI-only pseudo-channels. Picked field by field
 *  so the wire object is the contract's, not ours. */
const wirePerChannel = (pc?: Record<string, string>) => pc ? {
  email: pc.email, sms: pc.sms, whatsapp: pc.whatsapp, push: pc.push, webhook: pc.webhook,
} : undefined;

const wireLocaleVariants = (m: MessageItem) => {
  const tr = m.translations || {};
  const out = Object.keys(tr).filter(localeOk).map((locale) => ({
    ...tr[locale], locale, perChannel: wirePerChannel(tr[locale].perChannel),
  }));
  return out.length ? out : undefined;
};

/** The seven fields the write contract does not model (CC 2026-09-03 §3). They
 *  were riding `.passthrough()`, were DROPPED with a 200 and named back in
 *  `warnings[]`, and become a 400 once the platform stops accepting them — so
 *  they are no longer sent (JAI-1380). Two consequences we are NOT hiding:
 *
 *  · `defaultLanguage` still reaches the wire, under its modelled name
 *    (`defaultLocale`), and per-locale content still rides `localeVariants`.
 *  · `whatsapp`, `ics`, `wallet` and `artefacts` hold REAL author content with
 *    no modelled home yet. Not sending them means they no longer round-trip a
 *    save; that is a contract gap, filed in the reply, not a UI decision. Until
 *    it closes, the composer's local cache is the only place they live. */
const UNMODELLED_FIELDS = ["languages", "defaultLanguage", "ics", "wallet", "artefacts", "audience", "whatsapp"] as const;

/** Fields the APPOINTMENT store cannot hold (CC 2026-09-03, PR #1652): there is
 *  no raw-HTML column on it, and locale content is one row per locale upstream.
 *  Both are dropped-and-warned rather than 400, and both have their own docket —
 *  so they are left out of an appointment item's PATCH instead of being sent to
 *  be named back. A library message still sends both. */
const APPT_UNSUPPORTED_FIELDS = ["emailHtml", "localeVariants", "translations"] as const;

/** The content half of a write, shared by create and update. */
function wireContent(m: MessageItem): Record<string, unknown> {
  const { id: _id, status: _status, updated: _updated, lineage: _lineage, trigger: _trigger, offsetMinutes: _off, group: _group, ...rest } = m;
  const variants = wireLocaleVariants(m);
  const out: Record<string, unknown> = {
    ...rest,
    channels: (m.channels || []).filter((c) => WIRE_CHANNELS.has(c)),
    perChannel: wirePerChannel(m.perChannel),
    ...(m.defaultLanguage && localeOk(m.defaultLanguage) ? { defaultLocale: m.defaultLanguage } : {}),
    ...(variants ? { localeVariants: variants } : {}),
  };
  UNMODELLED_FIELDS.forEach((k) => { delete out[k]; });
  if (m.lineage === "appointment_comms") APPT_UNSUPPORTED_FIELDS.forEach((k) => { delete out[k]; });
  return out;
}

/** Our two-status UI lifecycle → the contract's. `approved` renders as "Live". */
const wireStatus = (s: MessageItem["status"]) => (s === "draft" ? "draft" : "approved");

/** The lifecycle command a save implies, derived from the CACHED row — a status
 *  edit is a transition, and only a transition may move it. No change in status
 *  means no command, which is the normal autosave. */
function commandFor(prev: MessageItem | undefined, next: MessageItem) {
  if (!prev || prev.status === next.status) return undefined;
  return next.status === "live" ? ("make_live" as const) : ("back_to_draft" as const);
}

function fromServer(s: Record<string, any>): MessageItem {
  return {
    id: s.id, name: s.name ?? "", channels: withPseudo(s.channels ?? ["email"], s),
    /* Appointment messages arrive in the same list under their own lineage
       (PR #1652). `trigger` + `offsetMinutes` is the row's identity; `group` is
       server-derived and drives the Appointments band. */
    lineage: s.lineage === "appointment_comms" ? "appointment_comms" : "library",
    trigger: s.trigger ?? undefined,
    offsetMinutes: s.offsetMinutes ?? null,
    group: s.group ?? undefined,
    preheader: s.preheader ?? undefined,
    status: normStatus(s.status ?? "draft"), audience: s.audience ?? "All",
    updated: humanWhen(s.updatedAt ?? s.updated ?? "—"), subject: s.subject ?? "",
    perChannel: s.perChannel ?? {}, body: s.body ?? undefined,
    whatsapp: s.whatsapp ?? undefined, quickReplies: s.quickReplies ?? undefined,
    // REQ-112: the multilingual + email-design fields. These were dropped here,
    // so a server row carrying an emailDesign arrived with none and every seeded
    // email fell back to plain text — which read as "the logo isn't displaying"
    // (operator, 2026-07-31). Anything the server sends now survives the mapping.
    // What the SERVER does with them on the way IN is a different question: the
    // deployed store builds the stored definition from named contract fields
    // only, so these ride `.passthrough()` and are dropped with a 200 today
    // (CC 2026-09-01 §3, being fixed). The serving engine keeps them, which is
    // why a design round-trips in the preview and would not have in production.
    languages: s.languages ?? undefined,
    defaultLanguage: s.defaultLanguage ?? undefined,
    translations: s.translations ?? undefined,
    emailDesign: s.emailDesign ?? undefined,
    emailHtml: s.emailHtml ?? undefined,
    ics: s.ics ?? undefined,
    wallet: s.wallet ?? undefined,
    /* Artefact modes (2026-09-01). Left UNDEFINED when the server has none, so
       `artefactsOf` can apply the migration defaults — writing `off` here would
       silently turn off the .ics attachment that ships unconditionally today. */
    artefacts: s.artefacts ?? undefined,
  };
}

function overlaySeed(server: Record<string, any>[]): MessageItem[] {
  // Seed is empty under the seed-guard; keep the overlay so any locally-cached
  // content survives a refetch, and msg_* referenced ids resolve when the mock
  // layer serves them (REQ-017).
  const byId = new Map<string, MessageItem>([].map((m: MessageItem) => [m.id, { ...m }]));
  const extra: MessageItem[] = [];
  server.forEach((s: Record<string, any>) => {
    const id = s.id;
    const existing = id ? byId.get(id) : undefined;
    if (existing) {
      existing.name = s.name ?? existing.name;
      existing.channels = withPseudo(s.channels ?? existing.channels, s);
      if (s.lineage) existing.lineage = s.lineage === "appointment_comms" ? "appointment_comms" : "library";
      if (s.trigger) existing.trigger = s.trigger;
      if (s.offsetMinutes !== undefined) existing.offsetMinutes = s.offsetMinutes;
      if (s.group) existing.group = s.group;
      if (s.preheader != null) existing.preheader = s.preheader;
      existing.status = normStatus(s.status ?? existing.status);
      existing.audience = s.audience ?? existing.audience;
      existing.updated = humanWhen(s.updatedAt ?? s.updated ?? existing.updated);
      // Same fields as fromServer — a refetch must not strip a design or a
      // language set off a row the cache already knows about.
      if (s.perChannel) existing.perChannel = s.perChannel;
      if (s.subject != null) existing.subject = s.subject;
      if (s.languages) existing.languages = s.languages;
      if (s.defaultLanguage) existing.defaultLanguage = s.defaultLanguage;
      if (s.translations) existing.translations = s.translations;
      if (s.emailDesign) existing.emailDesign = s.emailDesign;
      if (s.emailHtml) existing.emailHtml = s.emailHtml;
      if (s.ics) existing.ics = s.ics;
      if (s.wallet) existing.wallet = s.wallet;
      if (s.artefacts) existing.artefacts = s.artefacts;
    } else if (id) {
      extra.push(fromServer(s));
    }
  });
  return [...extra, ...byId.values()];
}

async function loadAll() {
  /* A failed read must not look like "you have no messages" (2026-07-31). The
     typed op's path has moved between kit versions; when it fails we retry the
     canonical library path directly through the same client choke point, and only
     then record a real error. */
  let arr: Record<string, any>[] = [];
  let failed: unknown = null;
  const r = await listBuilderMessages().catch((e) => { failed = e; console.warn("listBuilderMessages failed:", e); return null; });
  arr = r ? ((r as any).messages ?? (r as any).data ?? []) : [];
  if (!arr.length) {
    try {
      const alt = await apiRaw<{ messages?: Record<string, any>[]; data?: Record<string, any>[] }>("/v1/admin/messages");
      arr = alt?.messages ?? alt?.data ?? [];
      if (arr.length) { failed = null; console.warn("listBuilderMessages returned nothing; served the library from /v1/admin/messages instead."); }
    } catch (e) {
      if (failed) console.warn("message library unavailable from both paths:", e);
      else failed = arr.length ? null : failed;
    }
  }
  _loadError = arr.length ? null : (failed ? String((failed as { message?: string }).message || failed) : null);
  _cache = overlaySeed(arr);
  notify();
}

export function useMessaging() {
  const [, setV] = useState(0);
  useEffect(() => {
    const fn = () => setV((v) => v + 1);
    _listeners.add(fn);
    if (!_cache) loadAll();
    return () => { _listeners.delete(fn); };
  }, []);

  return {
    all: (): MessageItem[] => _cache ?? [],
    loading: _cache === null,
    /** Set when the last read failed. Screens MUST distinguish this from an empty
     *  library — rendering "no messages yet" over a failed read is how two
     *  separate faults hid in plain sight on 2026-07-31. */
    error: _loadError,
    /** Fields the platform dropped or adjusted on the last write. */
    warnings: (): MsgWarning[] => _lastWarnings,
    get: (id: string) => (_cache ?? []).find((m) => m.id === id) ?? null,
    newMessage: (): MessageItem => ({ id: tmpId(), name: "", channels: ["email"], status: "draft", audience: "All", updated: "Just now", subject: "", perChannel: {}, body: "" }), // ui-local — unsaved draft shape
    /** Awaited client write first; cache+notify only on success (#728). */
    upsert: async (m: MessageItem): Promise<MessageItem> => {
      const idx = (_cache ?? []).findIndex((x) => x.id === m.id);
      const isNew = idx < 0 || m.id.startsWith("m_new_");
      const saved: MessageItem = { ...m, updated: "Just now" };
      const prev = idx >= 0 ? (_cache ?? [])[idx] : undefined;
      const cmd = commandFor(prev, m);
      const res = (isNew
        ? await createMessage({ ...wireContent(saved), status: wireStatus(saved.status) } as unknown as StudioMessageCreateInput)
        : await updateMessage(m.id, { ...wireContent(saved), ...(cmd ? { command: cmd } : {}) } as unknown as StudioMessageUpdateInput)
      ) as Record<string, any> | undefined;
      _lastWarnings = warningsOf(res);
      const sid = res?.id ?? res?.message?.id;
      if (isNew && sid) saved.id = String(sid);
      const j = (_cache ?? []).findIndex((x) => x.id === m.id);
      logUpsert("message", j >= 0 ? ((_cache ?? [])[j] as unknown as Record<string, unknown>) : null, saved as unknown as Record<string, unknown>, { name: saved.name });
      _cache = j >= 0 ? (_cache ?? []).map((x) => (x.id === m.id ? saved : x)) : [saved, ...(_cache ?? [])];
      notify();
      return saved;
    },
    duplicate: async (id: string): Promise<MessageItem | null> => {
      const s = (_cache ?? []).find((x) => x.id === id);
      if (!s) return null;
      const c: MessageItem = { ...s, id: tmpId(), name: i18nT("messaging.useMessaging.sNameCopy.icu", { value0: s.name }), status: "draft", updated: "Just now" };
      const res = await duplicateMessage(id, { name: c.name }) as Record<string, any> | undefined;
      const sid = res?.id ?? res?.message?.id;
      if (sid) c.id = String(sid);
      _cache = [c, ...(_cache ?? [])];
      notify();
      return c;
    },
    remove: async (id: string) => {
      await deleteMessage(id);
      
      const prev = (_cache ?? []).find((x) => x.id === id);
      if (prev) logDelete("message", prev as unknown as Record<string, unknown>, { name: prev.name });
      _cache = (_cache ?? []).filter((x) => x.id !== id);
      notify();
    },
    reload: () => { _cache = null; _loadError = null; loadAll(); },
  };
}

/** Count of library messages, loading the cache if needed (for the rail tabs). */
export function useMessageCount(): number {
  const store = useMessaging();
  return store.all().length;
}
